import React from 'react';
import { Award, Clock, Heart, Zap } from 'lucide-react';
import { Reveal } from './Reveal';

const About = () => {
  const features = [
    {
      icon: Award,
      title: 'Professional Quality',
      description: 'Industry-standard editing techniques and tools for exceptional results'
    },
    {
      icon: Clock,
      title: 'Fast Turnaround',
      description: '1-3 week delivery times to keep your content fresh and timely'
    },
    {
      icon: Heart,
      title: 'Client Focused',
      description: 'Dedicated support and revisions to ensure your complete satisfaction'
    },
    {
      icon: Zap,
      title: 'Versatile Services',
      description: 'From short-form verticals to long-form horizontals and everything in between'
    }
  ];

  const reasons = [
    {
      title: 'Specialized Video Editing Expertise',
      description: 'We focus exclusively on video editing, allowing us to deliver consistently high-quality results with precision, clarity, and creative control.'
    },
    {
      title: 'Hands-On Marketing Guidance',
      description: 'Beyond editing, we work closely with you to ensure your message is accurate, intentional, and aligned with your marketing goals—so nothing gets lost in translation.'
    },
    {
      title: 'Transforming Raw Footage Into Impact',
      description: 'Your raw files are shaped into polished, compelling videos that look incredible and communicate clearly across every platform they appear on.'
    },
    {
      title: 'Message-First Storytelling',
      description: 'Every cut, sound choice, and pacing decision is made to support clear communication, audience retention, and brand credibility.'
    }
  ];

  return (
    <section id="about" className="section-padding bg-[#050505] border-t border-white/5" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center max-w-3xl mx-auto mb-16">
          <p className="overline mb-5">Why Go-To Media</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6">
            We elevate your <span className="gold-gradient">content.</span>
          </h2>
          <p className="text-lg md:text-xl text-[#a0a0a0] leading-relaxed">
            At Go-To Media Collective, we&apos;re passionate about transforming your vision into compelling
            visual stories tailored to your brand&apos;s unique needs.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Reveal key={index} delay={index * 0.08}>
                <div className="h-full bg-[#0f0f0f] rounded-2xl p-8 border border-white/5 hover:border-[#d4af37]/40 transition-colors duration-500">
                  <Icon size={36} className="text-[#d4af37] mb-5" strokeWidth={1.25} />
                  <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                  <p className="text-[#a0a0a0] leading-relaxed">{feature.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Why Choose Us */}
        <Reveal>
          <div className="mt-16 bg-[#0f0f0f] rounded-3xl p-8 sm:p-12 border border-white/5">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="font-display text-3xl md:text-4xl font-semibold mb-8 text-white">
                  Why <span className="gold-gradient">choose us?</span>
                </h3>
                <div className="space-y-6">
                  {reasons.map((reason, index) => (
                    <div key={index} className="feature-item flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#d4af37] mt-2.5 flex-shrink-0"></div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{reason.title}</h4>
                        <p className="text-[#a0a0a0] leading-relaxed">{reason.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative flex items-center justify-center">
                <div className="rounded-2xl flex items-center justify-center border border-white/5 bg-[#050505] p-8 w-full">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clipcraft-270/artifacts/w90rr2en_GTMC_Logo_motto-removebg-preview.png"
                    alt="Go-To Media Collective - Production, Creativity, Excellence"
                    className="max-w-full h-auto w-full"
                    style={{ maxHeight: '400px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Recommendations */}
        <Reveal>
          <div className="mt-16 bg-[#0f0f0f] rounded-3xl p-8 sm:p-12 border border-white/5">
            <div className="text-center mb-8">
              <h3 className="font-display text-3xl md:text-4xl font-semibold mb-4 text-white">
                For Video &amp; Photography <span className="gold-gradient">Recommendations</span>
              </h3>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-8 my-8">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/5 p-6 hover:border-[#d4af37]/40 transition-all duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clipcraft-270/artifacts/a4d7bzta_CROSSGATE_lgo_WHITE_screen_file.webp"
                    alt="Crossgate Films"
                    className="h-20 w-auto"
                  />
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/5 p-6 hover:border-[#d4af37]/40 transition-all duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clipcraft-270/artifacts/g6djip3i_2013%20logo%20white%20medium_edited_edited%20%281%29.png"
                    alt="Chase Photography"
                    className="h-20 w-auto brightness-0 invert"
                  />
                </div>
              </div>

              <div className="max-w-3xl mx-auto text-left">
                <h4 className="text-xl font-semibold text-white mb-4">No videographer? No problem.</h4>
                <p className="text-[#a0a0a0] mb-4 leading-relaxed">
                  If you&apos;ll be recording the footage yourself, we&apos;re happy to share recommended camera options
                  and filming guidelines to help you capture the best possible footage.
                </p>
                <p className="text-[#a0a0a0] text-sm">
                  Feel free to reach out to confirm video quality requirements before recording.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default About;
